import React, { useState } from 'react';
import { Search, Download, Book, Loader, Play } from 'lucide-react';
import { crawlerAPI } from '../../services/api';
import toast from 'react-hot-toast';
import './Admin.css';

const CrawlerPage = () => {
  const [url, setUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [novelInfo, setNovelInfo] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [importSettings, setImportSettings] = useState({
    startChapter: 1,
    endChapter: '',
    delay: 1500
  });

  const handleFetchInfo = async () => {
    if (!url.trim()) {
      toast.error('Please enter novel URL');
      return;
    }

    setIsLoading(true);
    setNovelInfo(null);
    setImportResult(null);
    
    try {
      const response = await crawlerAPI.getNovelInfo(url);
      setNovelInfo(response.data);
      setImportSettings(prev => ({
        ...prev,
        endChapter: response.data.chapterCount || ''
      }));
      toast.success(`Found: ${response.data.title}`);
    } catch (error) {
      toast.error(error.message || 'Failed to fetch novel info');
    } finally {
      setIsLoading(false);
    }
  };

  const handleImportNovel = async () => {
    if (!url.trim()) {
      toast.error('Please fetch novel info first');
      return;
    }

    setIsImporting(true);
    setImportResult(null);
    
    try {
      const response = await crawlerAPI.importNovel({
        url: url,
        startChapter: parseInt(importSettings.startChapter) || 1,
        endChapter: importSettings.endChapter ? parseInt(importSettings.endChapter) : null,
        delay: parseInt(importSettings.delay) || 1500,
        autoCreateNovel: true
      });
      
      setImportResult(response.data);
      toast.success(`Imported ${response.data.imported} chapters!`);
    } catch (error) {
      toast.error(error.message || 'Failed to import novel');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="crawler-page">
      <div className="admin-page-header">
        <h1>🕷️ Novel Crawler</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: '8px' }}>
          Import novels and chapters from supported sources
        </p>
      </div>

      {/* URL Input */}
      <div className="crawler-input-section">
        <div className="input-group">
          <input
            type="text"
            placeholder="Enter novel URL (e.g., https://www.freekingbook.com/novels/...)"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleFetchInfo()}
          />
          <button 
            className="btn-fetch" 
            onClick={handleFetchInfo}
            disabled={isLoading}
          >
            {isLoading ? <Loader className="spin" size={18} /> : <Search size={18} />}
            {isLoading ? 'Fetching...' : 'Fetch Info'}
          </button>
        </div>
        
        <div className="supported-sources">
          <span>Supported sources:</span>
          <span className="source-badge">freekingbook.com</span>
        </div>
      </div>

      {/* Novel Info */}
      {novelInfo && (
        <div className="novel-preview">
          <div className="preview-header">
            <div className="preview-cover">
              {novelInfo.cover ? (
                <img src={novelInfo.cover} alt={novelInfo.title} />
              ) : (
                <div className="cover-placeholder">
                  <Book size={40} />
                </div>
              )}
            </div>
            <div className="preview-info">
              <h2>{novelInfo.title}</h2>
              <p className="author">Author: {novelInfo.author}</p>
              <div className="meta-row">
                <span className={`status ${novelInfo.status}`}>
                  {novelInfo.status}
                </span>
                <span className="chapters">
                  {novelInfo.chapterCount} chapters
                </span>
              </div>
              <div className="genres">
                {novelInfo.genres?.map((g, i) => (
                  <span key={i} className="genre-tag">{g}</span>
                ))}
              </div>
              {novelInfo.description && (
                <p className="description">{novelInfo.description.substring(0, 200)}...</p>
              )}
            </div>
          </div>

          {/* Import Settings */}
          <div className="import-settings">
            <h4>📥 Import Settings</h4>
            <div className="settings-row">
              <div className="setting-item">
                <label>Start Chapter</label>
                <input
                  type="number"
                  min="1"
                  value={importSettings.startChapter}
                  onChange={(e) => setImportSettings({
                    ...importSettings,
                    startChapter: e.target.value
                  })}
                />
              </div>
              <div className="setting-item">
                <label>End Chapter</label>
                <input
                  type="number"
                  min="1"
                  placeholder="All"
                  value={importSettings.endChapter}
                  onChange={(e) => setImportSettings({
                    ...importSettings,
                    endChapter: e.target.value
                  })}
                />
              </div>
              <div className="setting-item">
                <label>Delay (ms)</label>
                <input
                  type="number"
                  min="500"
                  step="100"
                  value={importSettings.delay}
                  onChange={(e) => setImportSettings({
                    ...importSettings,
                    delay: e.target.value
                  })}
                />
              </div>
            </div>
            
            <button 
              className="btn-import" 
              onClick={handleImportNovel}
              disabled={isImporting}
            >
              {isImporting ? (
                <>
                  <Loader className="spin" size={18} />
                  Importing... (this may take a while)
                </>
              ) : (
                <>
                  <Download size={18} />
                  Import Novel + Chapters
                </>
              )}
            </button>
            
            <p className="import-note">
              ⚡ This will create the novel and import all chapters automatically.
              <br />
              ⏱️ Estimated time: ~{Math.ceil((importSettings.endChapter || novelInfo.chapterCount) * (importSettings.delay / 1000) / 60)} minutes
            </p>
          </div>
        </div>
      )}

      {/* Import Result */}
      {importResult && (
        <div className="import-result">
          <h3>✅ Import Complete!</h3>
          <div className="result-stats">
            <div className="result-item success">
              <span className="value">{importResult.imported}</span>
              <span className="label">Imported</span>
            </div>
            <div className="result-item warning">
              <span className="value">{importResult.skipped}</span>
              <span className="label">Skipped</span>
            </div>
            <div className="result-item error">
              <span className="value">{importResult.errors?.length || 0}</span>
              <span className="label">Errors</span>
            </div>
            <div className="result-item total">
              <span className="value">{importResult.totalChapters}</span>
              <span className="label">Total Chapters</span>
            </div>
          </div>
          
          {importResult.novel && (
            <div className="novel-link">
              <p>Novel created: <strong>{importResult.novel.title}</strong></p>
              <a 
                href={`/novel/${importResult.novel.slug}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn-view"
              >
                <Play size={16} /> View Novel
              </a>
            </div>
          )}
          
          {importResult.errors?.length > 0 && (
            <div className="error-list">
              <h4>Errors:</h4>
              <ul>
                {importResult.errors.slice(0, 10).map((err, i) => (
                  <li key={i}>Chapter {err.chapter}: {err.error}</li>
                ))}
                {importResult.errors.length > 10 && (
                  <li>...and {importResult.errors.length - 10} more</li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      <style jsx>{`
        .crawler-page {
          padding: 24px;
        }
        
        .admin-page-header {
          margin-bottom: 32px;
        }
        
        .admin-page-header h1 {
          margin: 0;
          font-size: 1.8rem;
        }
        
        .crawler-input-section {
          background: var(--bg-card);
          padding: 24px;
          border-radius: var(--radius-lg);
          margin-bottom: 24px;
        }
        
        .input-group {
          display: flex;
          gap: 12px;
        }
        
        .input-group input {
          flex: 1;
          padding: 14px 18px;
          background: var(--bg-dark);
          border: 1px solid var(--border);
          border-radius: var(--radius-sm);
          color: var(--text-primary);
          font-size: 1rem;
        }
        
        .btn-fetch {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 14px 24px;
          background: var(--primary);
          color: white;
          border: none;
          border-radius: var(--radius-sm);
          cursor: pointer;
          font-weight: 500;
          white-space: nowrap;
        }
        
        .btn-fetch:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .supported-sources {
          margin-top: 12px;
          font-size: 0.85rem;
          color: var(--text-muted);
          display: flex;
          align-items: center;
          gap: 8px;
        }
        
        .source-badge {
          background: var(--bg-dark);
          padding: 4px 10px;
          border-radius: 20px;
          color: var(--primary);
        }
        
        .novel-preview {
          background: var(--bg-card);
          border-radius: var(--radius-lg);
          padding: 24px;
          margin-bottom: 24px;
        }
        
        .preview-header {
          display: flex;
          gap: 24px;
          margin-bottom: 24px;
        }
        
        .preview-cover {
          width: 140px;
          height: 190px;
          border-radius: var(--radius-sm);
          overflow: hidden;
          flex-shrink: 0;
          background: var(--bg-dark);
        }
        
        .preview-cover img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .cover-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--text-muted);
        }
        
        .preview-info h2 {
          margin: 0 0 8px;
          font-size: 1.4rem;
        }
        
        .preview-info .author {
          color: var(--text-secondary);
          margin-bottom: 12px;
        }
        
        .preview-info .description {
          color: var(--text-muted);
          font-size: 0.9rem;
          line-height: 1.5;
          margin-top: 12px;
        }
        
        .meta-row {
          display: flex;
          gap: 12px;
          margin-bottom: 12px;
        }
        
        .meta-row .status {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.85rem;
          text-transform: capitalize;
        }
        
        .meta-row .status.completed {
          background: rgba(34, 197, 94, 0.2);
          color: #22c55e;
        }
        
        .meta-row .status.ongoing {
          background: rgba(59, 130, 246, 0.2);
          color: #3b82f6;
        }
        
        .meta-row .chapters {
          color: var(--text-muted);
        }
        
        .genres {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        
        .genre-tag {
          background: var(--bg-dark);
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 0.85rem;
          color: var(--text-secondary);
        }
        
        .import-settings {
          border-top: 1px solid var(--border);
          padding-top: 24px;
        }
        
        .import-settings h4 {
          margin: 0 0 16px;
          font-size: 1rem;
        }
        
        .settings-row {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 20px;
        }
        
        .setting-item label {
          display: block;
          margin-bottom: 6px;
          font-size: 0.85rem;
          color: var(--text-secondary);
        }
        
        .setting-item input {
          width: 100%;
          padding: 12px 14px;
          background: var(--bg-dark);
          border: 1px solid var(--border);
          border-radius: var(--radius-sm);
          color: var(--text-primary);
        }
        
        .btn-import {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          padding: 16px;
          background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
          color: white;
          border: none;
          border-radius: var(--radius-sm);
          cursor: pointer;
          font-weight: 600;
          font-size: 1.1rem;
          margin-bottom: 12px;
        }
        
        .btn-import:hover {
          opacity: 0.95;
        }
        
        .btn-import:disabled {
          background: #6b7280;
          cursor: not-allowed;
        }
        
        .import-note {
          font-size: 0.85rem;
          color: var(--text-muted);
          text-align: center;
          line-height: 1.6;
        }
        
        .import-result {
          background: var(--bg-card);
          border-radius: var(--radius-lg);
          padding: 24px;
          border: 1px solid #22c55e;
        }
        
        .import-result h3 {
          margin: 0 0 20px;
          color: #22c55e;
        }
        
        .result-stats {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          margin-bottom: 20px;
        }
        
        .result-item {
          text-align: center;
          padding: 16px;
          background: var(--bg-dark);
          border-radius: var(--radius-sm);
        }
        
        .result-item .value {
          display: block;
          font-size: 1.8rem;
          font-weight: 700;
        }
        
        .result-item .label {
          font-size: 0.85rem;
          color: var(--text-muted);
        }
        
        .result-item.success .value { color: #22c55e; }
        .result-item.warning .value { color: #f59e0b; }
        .result-item.error .value { color: #ef4444; }
        .result-item.total .value { color: var(--primary); }
        
        .novel-link {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px;
          background: var(--bg-dark);
          border-radius: var(--radius-sm);
          margin-bottom: 16px;
        }
        
        .btn-view {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 10px 20px;
          background: var(--primary);
          color: white;
          text-decoration: none;
          border-radius: var(--radius-sm);
          font-weight: 500;
        }
        
        .error-list {
          background: rgba(239, 68, 68, 0.1);
          border-radius: var(--radius-sm);
          padding: 16px;
        }
        
        .error-list h4 {
          margin: 0 0 12px;
          color: #ef4444;
        }
        
        .error-list ul {
          margin: 0;
          padding-left: 20px;
          color: var(--text-secondary);
          font-size: 0.9rem;
        }
        
        .error-list li {
          margin-bottom: 4px;
        }
        
        .spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .settings-row {
            grid-template-columns: 1fr;
          }
          
          .preview-header {
            flex-direction: column;
          }
          
          .result-stats {
            grid-template-columns: repeat(2, 1fr);
          }
        }
      `}</style>
    </div>
  );
};

export default CrawlerPage;
